import React, {useState, useEffect} from 'react';
import SwitchMUI from '../../../shared/SwitchMUI';
import CompaniesHero from '../../Components/Companies/CompaniesHero';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {getCompanyNotifications, patchCompanyNotifications} from '../../../redux/actions/company/companyActions';
import ButtonMUI from '../../../shared/ButtonMUI';
import {notifySuccess} from '../../../helpers/notifySnack';
import LoadPanel from '../../Components/LoadPanel';
import './CompaniesNotification.scss';
import {hasPermission} from "../../../helpers/functions";
import {PERMISSIONS} from "../../../helpers/constants";

const CompaniesNotification = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [buttonLoad, setButtonLoad] = useState(false);

  const [settings, setSettings] = useState({
    photoFeedback: true,
    videoFeedback: true,
    blueprint: true,
    documents: true,
    estimate: true,
    membersEdited: true,
    messages: true
  });
  const {profileMe} = useSelector((state) => state.profile);

  const permissionEstimate = hasPermission(PERMISSIONS.ESTIMATE_LIST, profileMe?.permissions ?? []);
  const [initialSettings, setInitialSettings] = useState(settings);

  useEffect(() => {
    dispatch(getCompanyNotifications(singleCompany.id));
  }, [dispatch]);

  const {singleCompany, companiesNotifications, companiesNotificationsLoading} = useSelector((state) => state.company);

  const notification = [
    {
      title: t('profileNotification.notifications.photoReportTitle'),
      descriptions: t('profileNotification.notifications.photoReport'),
      key: 'photoFeedback'
    },
    {
      title: t('profileNotification.notifications.videoReportTitle'),
      descriptions: t('profileNotification.notifications.videoReport'),
      key: 'videoFeedback'
    },
    {
      title: t('profileNotification.notifications.drawingsTitle'),
      descriptions: t('profileNotification.notifications.drawings'),
      key: 'blueprint'
    },
    {
      title: t('profileNotification.notifications.documentsTitle'),
      descriptions: t('profileNotification.notifications.documents'),
      key: 'documents'
    },
    ...(permissionEstimate
        ? [
          {
            title: t('profileNotification.notifications.estimateTitle'),
            descriptions: t('profileNotification.notifications.estimate'),
            key: 'estimate'
          }
        ]
        : []),
    {
      title: t('profileNotification.notifications.participantChangesTitle'),
      descriptions: t('profileNotification.notifications.participantChanges'),
      key: 'membersEdited'
    } ,
    {
      title: t('profileNotification.notifications.chat'),
      descriptions: t('profileNotification.notifications.chatMessages'),
      key: 'messages'
    }
  ];

  useEffect(() => {
    if (companiesNotifications && Object.keys(companiesNotifications).length) {
      setSettings(companiesNotifications);
      setInitialSettings(companiesNotifications);
    }
  }, [companiesNotifications]);

  const handleSwitchChange = (key) => (event) => {
    setSettings({
      ...settings,
      [key]: event.target.checked
    });
  };

  const handleNotificationRead = async () => {
    const updatedSettings = {
      photoFeedback: false,
      videoFeedback: false,
      blueprint: false,
      documents: false,
      estimate: false,
      membersEdited: false,
      messages: false
    };

    try {
      const response = await dispatch(patchCompanyNotifications(updatedSettings, singleCompany.id));
      if (response.payload?.status === 200) {
        setSettings(updatedSettings);
        setInitialSettings(updatedSettings);
        notifySuccess(t('all.successfully'));
      }
    }
    finally {
    }
  };

  const handleSaveSettings = async () => {
    setButtonLoad(true);
    try {
      const response = await dispatch(patchCompanyNotifications(settings, singleCompany.id));
      if (response.payload?.status === 200) {
        setInitialSettings(settings);
        dispatch(getCompanyNotifications(singleCompany.id));
        notifySuccess(t('all.successfully'));

      }
    } finally {
      setButtonLoad(false);
    }
  };

  if (companiesNotificationsLoading) {
    return (
      <div className={'companies-page-loader'}>
        <LoadPanel noBg={true} circleSize={50} staticPos={true} />
      </div>
    );
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(initialSettings);

  return (
    <div className='companies__notification'>
      <div className='companies__notification-name'>{singleCompany?.name ?? ''}</div>

      <CompaniesHero
        heading={t('companiesNotification.companyNotification.heading')}
        subHeading={t('companiesNotification.companyNotification.subHeading')}
        notification={true}
        handleNotificationRead={handleNotificationRead}
      />

      <div className='companies__notification-content'>
        <div className='companies__notification-list'>
          {notification.map(({title, descriptions, key}, idx) => (
            <div key={idx} className='switch'>
              <SwitchMUI color='primary' checked={settings[key]} value={key} onChange={handleSwitchChange(key)} />
              <div className='switch__content'>
                <div className='switch__title'>{title}</div>
                <div className='switch__descriptions'>{descriptions}</div>
              </div>
            </div>
          ))}
        </div>

        <ButtonMUI onClick={handleSaveSettings} loading={buttonLoad} disabled={!hasChanges}>
          {t('companiesNotification.saveButton')}
        </ButtonMUI>
      </div>
    </div>
  );
};

export default CompaniesNotification;
